/**
 * 광고성 메시지 발송 훅 — 테스트 발송 + 엑셀 수신자 업로드.
 *
 * 대량 발송 자체는 기존 `useBulkDispatchMutation`(/admin/v1/notification/dispatch/bulk, 라이브
 * 계약) 재사용. 여기서는 추가 표면(테스트/엑셀)만 정의. ⚠️ 엑셀 경로는 be-notif Task#3 확정 후 wire.
 */
import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import type { NotificationChannel } from '../types/notificationMessage';

const ROOT = '/admin/v1/notification';

interface Envelope<T> {
  success: boolean;
  data?: T | null;
  error?: { code?: string; message?: string } | null;
}

const readEnvelope = async <T>(res: Response, fallback: string): Promise<T> => {
  let env: Envelope<T> | null = null;
  try {
    env = (await res.json()) as Envelope<T>;
  } catch {
    env = null;
  }
  if (!res.ok || !env || env.success !== true || env.data == null) {
    throw new Error(env?.error?.message ?? fallback);
  }
  return env.data;
};

export interface TestSendInput {
  channel: NotificationChannel;
  /** 단일 수신처(이메일/전화번호). */
  recipient: string;
  templateCode?: string;
  rawSubject?: string;
  rawBody?: string;
  variables?: Record<string, string>;
}

/** 테스트 발송 — 단건 메시지 endpoint(/messages) 재사용(external 수신자). */
export const useTestSendMutation = (): UseMutationResult<void, Error, TestSendInput> =>
  useMutation({
    mutationFn: async (input) => {
      const path = `${ROOT}/messages`;
      const body = {
        channel: input.channel,
        recipient: { external: input.recipient },
        ...(input.templateCode ? { templateCode: input.templateCode } : {}),
        ...(input.rawSubject ? { rawSubject: input.rawSubject } : {}),
        ...(input.rawBody ? { rawBody: input.rawBody } : {}),
        ...(input.variables ? { variables: input.variables } : {}),
      };
      const res = await adminFetch(path, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', path, body) },
        body: JSON.stringify(body),
      });
      await readEnvelope<unknown>(res, '테스트 발송에 실패했습니다');
    },
  });

/** 업로드 파싱 결과 — be 계약 정합: recipients[{external,variables}] + rowCount + skippedCount. */
export interface ExcelUploadResult {
  /** 파싱된 외부 수신처(이메일/전화) 목록. */
  recipients: string[];
  /** 전체 데이터 행 수. */
  rowCount: number;
  /** 건너뛴 행 수(빈 연락처/빈 줄). */
  skippedCount: number;
}

interface UploadRecipientWire {
  external?: string | null;
  variables?: Record<string, string> | null;
}

/**
 * 수신자 CSV 업로드 — multipart `file`(.csv UTF-8). 응답 `{recipients:[{external,variables}],
 * rowCount, skippedCount}` (be 공표 — invalidCount 아님, skippedCount). 발송은 이 recipients 를
 * /dispatch/bulk 로 전달. 파싱오류 400 NOTIFICATION_RECIPIENT_FILE_INVALID.
 */
export const useExcelUploadMutation = (): UseMutationResult<ExcelUploadResult, Error, File> =>
  useMutation({
    mutationFn: async (file) => {
      const path = `${ROOT}/dispatch/recipients/upload`;
      const fd = new FormData();
      fd.append('file', file);
      // multipart 이므로 Content-Type 은 브라우저가 boundary 와 함께 설정해야 한다 —
      // body 가 FormData 면 adminFetch 가 빼주므로 호출부에서 헤더를 손대지 않는다.
      const res = await adminFetch(path, {
        method: 'POST',
        body: fd,
      });
      const data = await readEnvelope<{
        recipients?: UploadRecipientWire[];
        rowCount?: number;
        skippedCount?: number;
      }>(res, '엑셀 업로드에 실패했습니다');
      const recipients = (data.recipients ?? [])
        .map((r) => (typeof r.external === 'string' ? r.external.trim() : ''))
        .filter((e) => e.length > 0);
      return {
        recipients,
        rowCount: data.rowCount ?? recipients.length,
        skippedCount: data.skippedCount ?? 0,
      };
    },
  });
